import { Link } from "react-router-dom";
import { COMPANY } from "../data/company";
import SEO from "../components/SEO";
import { imageUrl } from "../lib/images";

export default function Services() {
  const serviceDetails = [
    {
      title: "Døre og vinduer",
      badge: "Vores store passion – det vi elsker at lave",
      slug: "doere-og-vinduer",
      path: "/ydelser/doere-og-vinduer",
      icon: "fa-solid fa-door-open",
      image: "/_assets/doere-vinduer-parcelhus.jpg",
      intro: "Vi elsker at lave døre og vinduer! Udskiftning af elementer forbedrer boligens indeklima, sænker varmeregningen og forvandler boligens æstetik. Vi sikrer grundig rådgivning, præcis opmåling og kompromisløs montagemæssig finish.",
      points: [
        "Udskiftning og montering af facadedøre, terrassedøre og skydedøre",
        "Moderne energieffektive træ/alu og klassiske træelementer",
        "Millimeterpræcist lod, vage og ubrudte, vejrbestandige fuger",
        "Elegante afslutninger med lysninger, sålbænke og gerigter",
      ],
    },
    {
      title: "Træterrasser og udendørsarealer",
      badge: "Gedigne uderum til udeliv",
      slug: "traeterrasser-og-udendoersarealer",
      path: "/ydelser/traeterrasser-og-udendoersarealer",
      icon: "fa-solid fa-layer-group",
      image: "/_assets/ny-traeterrasse-dansk-parcelhus-hero.jpg",
      intro: "En velkonstrueret træterrasse skaber et skønt samlingspunkt for familien. Vi opbygger skræddersyede terrasser og udendørsarealer med solid underkonstruktion, korrekt ventilation og smukke træsorter.",
      points: [
        "Nye hævede og terrænnære træterrasser i hårdttræ, lærk eller termotræ",
        "Gedigen underkonstruktion med god luftcirkulation for lang levetid",
        "Integrerede trapper, niveauskift, bænke og læhegn",
        "Præcis montage med skjulte skruer eller ensartede skruelinjer",
      ],
    },
    {
      title: "Indendørs renovering – lofter & vægge",
      badge: "Indvendig modernisering",
      slug: "indendoers-renovering",
      path: "/ydelser/indendoers-renovering",
      icon: "fa-solid fa-hammer",
      image: "/_assets/indendoers-renovering-lofter-vaegge.jpg",
      intro: "Fornyelse af lofter og vægge løfter hele boligens stemning og indeklima. Vi opsætter akustiklofter med smukke trælameller, glatte gipslofter og etablerer nye skillevægge med fokus på akustik og snorlige finish.",
      points: [
        "Opsætning af moderne akustiklofter i eg, ask eller valnød",
        "Montering af glatte gipslofter, troldtekt og loftsopretning",
        "Etablering, opbygning og flytning af indvendige skillevægge",
        "Lydisolering, gerigter, fodlister og indvendige døre",
      ],
    },
  ];

  return (
    <>
      <SEO
        title="Ydelser"
        description={`Se oversigten over tømrerarbejde hos ${COMPANY.name} i ${COMPANY.city}. Vi tilbyder døre & vinduer, træterrasser og udendørsarealer samt indendørs renovering af lofter og vægge.`}
        path="/ydelser"
        image="/_assets/doere-vinduer-parcelhus.jpg"
      />

      {/* Page Header */}
      <section className="bg-[#111111] text-white py-16 sm:py-20 border-b border-neutral-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2 text-[#A78E51] font-bold text-xs uppercase tracking-widest mb-3">
              <Link to="/" className="hover:underline">Forside</Link>
              <i className="fa-solid fa-angle-right text-[10px] text-neutral-500" aria-hidden="true" />
              <span>Ydelser</span>
            </div>
            <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-white">
              Vores tømrerydelser
            </h1>
            <p className="mt-4 text-base sm:text-lg text-neutral-300 leading-relaxed">
              Hos <strong>{COMPANY.name}</strong> er vi specialister i døre & vinduer – som vi elsker at lave – samt eksklusive træterrasser og gennemført indendørs renovering af lofter og vægge. Fællesnævneren er altid solidt håndværk og ordentlig rådgivning.
            </p>
          </div>
        </div>
      </section>

      {/* Service Blocks List */}
      <section className="py-16 sm:py-24 bg-[#FAFAF8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
          {serviceDetails.map((service, index) => {
            const isReversed = index % 2 === 1;

            return (
              <div
                key={service.slug}
                className="bg-white rounded-xl border border-neutral-200 overflow-hidden shadow-sm grid grid-cols-1 lg:grid-cols-12 gap-0 items-stretch"
              >
                {/* Image col */}
                <div
                  className={`lg:col-span-5 relative min-h-[280px] sm:min-h-[360px] ${
                    isReversed ? "lg:order-2" : "lg:order-1"
                  }`}
                >
                  <img
                    src={imageUrl(service.image, "width=900,quality=80,format=auto")}
                    alt={service.title}
                    className="w-full h-full object-cover absolute inset-0"
                    loading="lazy"
                  />
                  <div className="absolute top-4 left-4 bg-[#111111]/90 text-[#A78E51] p-3 rounded-md border border-[#A78E51]/40">
                    <i className={`${service.icon} text-lg`} aria-hidden="true" />
                  </div>
                </div>

                {/* Content col */}
                <div
                  className={`lg:col-span-7 p-8 sm:p-10 lg:p-12 flex flex-col justify-between ${
                    isReversed ? "lg:order-1" : "lg:order-2"
                  }`}
                >
                  <div>
                    <span className="text-[#A78E51] font-bold text-xs uppercase tracking-widest block mb-2">
                      Specialeområde
                    </span>
                    <h2 className="text-2xl sm:text-3xl font-extrabold text-[#111111] tracking-tight mb-4">
                      {service.title}
                    </h2>
                    <p className="text-neutral-600 text-sm sm:text-base leading-relaxed mb-6">
                      {service.intro}
                    </p>

                    <div className="mb-8">
                      <h3 className="text-xs font-bold uppercase tracking-wider text-neutral-900 mb-3">
                        Typiske opgaver inden for {service.title.toLowerCase()}:
                      </h3>
                      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                        {service.points.map((pt, i) => (
                          <li key={i} className="flex items-start gap-2.5 text-xs sm:text-sm text-neutral-700">
                            <i className="fa-solid fa-check text-[#A78E51] mt-1 shrink-0" aria-hidden="true" />
                            <span>{pt}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div className="pt-6 border-t border-neutral-100 flex flex-wrap items-center gap-4">
                    <Link
                      to="/kontakt"
                      className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-[#111111] text-white hover:bg-neutral-800 text-sm font-bold transition-colors"
                    >
                      <span>Få et tilbud</span>
                      <i className="fa-solid fa-arrow-right text-xs text-[#A78E51]" aria-hidden="true" />
                    </Link>

                    <Link
                      to={service.path}
                      className="inline-flex items-center gap-2 px-5 py-3 rounded-md border border-neutral-300 hover:border-[#A78E51] text-[#111111] text-sm font-semibold transition-colors"
                    >
                      <span>Læs mere om {service.title.toLowerCase()}</span>
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Direct Contact Banner */}
      <section className="bg-white py-14 border-t border-[#E5E5E0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center max-w-3xl">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-[#111111]">
            Har du en opgave, der ikke er nævnt her?
          </h2>
          <p className="mt-3 text-neutral-600 text-sm sm:text-base">
            Vi rådgiver gerne om tømrerarbejde på Fyn. Kontakt Andreas direkte for at høre mere om mulighederne for netop din ejendom.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-4">
            <a
              href={COMPANY.phoneLink}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-[#A78E51] text-white font-bold text-sm hover:bg-[#937B43] transition-colors"
            >
              <i className="fa-solid fa-phone" aria-hidden="true" />
              <span>Ring {COMPANY.phone}</span>
            </a>
            <Link
              to="/kontakt"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-[#111111] text-white font-bold text-sm hover:bg-neutral-800 transition-colors"
            >
              <span>Kontaktformular</span>
              <i className="fa-solid fa-envelope text-[#A78E51]" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
